import React from 'react';
import { TrendingUp, AlertTriangle, Bell, Info } from 'lucide-react';

const AlertsInsights = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-20 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-20 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-32 bg-gray-200 rounded animate-pulse"></div>
      </div>
    );
  }

  const portfolioHealth = data?.portfolio_health || [];
  const tariffChanges = data?.tariff_rate_changes || [];
  const optimizations = data?.optimization_opportunities || [];

  // Calculate totals for portfolio health
  const totalPrograms = portfolioHealth.reduce((sum, item) => sum + (item.program_count || 0), 0);

  return (
    <div className="space-y-6">
      {/* Portfolio Health Donut Chart */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
          <TrendingUp className="w-4 h-4" />
          <span>Portfolio Health</span>
        </h3>
        
        {totalPrograms > 0 ? (
          <div className="space-y-2">
            {portfolioHealth.map((item, idx) => {
              const percentage = totalPrograms > 0 ? ((item.program_count / totalPrograms) * 100).toFixed(0) : 0;
              let barColor = 'bg-gray-400';
              
              if (item.status === 'On Track') barColor = 'bg-green-500';
              else if (item.status === 'Needs Attention') barColor = 'bg-yellow-500';
              else if (item.status === 'High Risk') barColor = 'bg-red-500';

              return (
                <div key={idx} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">{item.status}</span>
                    <span className="font-medium text-gray-900">
                      {item.program_count} ({percentage}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`${barColor} h-2 rounded-full transition-all`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No active programs to display</p>
        )}
      </div>

      {/* Tariff Rate Changes */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
          <Bell className="w-4 h-4" />
          <span>Recent Tariff Changes</span>
        </h3>
        
        {tariffChanges.length > 0 ? (
          <div className="space-y-2">
            {tariffChanges.slice(0, 3).map((change, idx) => {
              const formatDate = (dateStr) => {
                if (!dateStr) return 'N/A';
                return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              };

              return (
                <div key={idx} className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-sm text-gray-900">
                        HTS {change.hts_code}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        Rate: {change.new_rate}% • Effective {formatDate(change.effective_date)}
                      </div>
                      <div className="text-xs text-orange-700 mt-1">
                        {change.affected_parts} parts affected
                      </div>
                    </div>
                    <AlertTriangle className="w-4 h-4 text-orange-600 flex-shrink-0" />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No recent tariff changes</p>
        )}
      </div>

      {/* Optimization Opportunities */}
      {optimizations.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
            <Info className="w-4 h-4" />
            <span>Optimization Opportunities</span>
          </h3>
          
          <div className="space-y-2">
            {optimizations.slice(0, 2).map((opp, idx) => (
              <div key={idx} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="font-medium text-sm text-gray-900 mb-1">
                  {opp.recommendation}
                </div>
                <div className="text-xs text-blue-700">
                  Potential savings: ${(opp.potential_savings / 100).toLocaleString()} {opp.timeframe}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertsInsights;
